import { Key, Tab, TabList, TabPanel, Tabs } from "react-aria-components";
import { EntityNames } from "../../../../enums/EntityNames";
import List from "../../List/List";
import GetSchema, { ISchemaChild } from "../../entityComponents/Schema";
import { EntityViewTypes } from "../../../../enums/EntityViewTypes";
import { IFilter } from "../../../../hooks/UseFilters";
import { FilterMatchType } from "../../../../enums/FilterMatchType";
import { PropertyTypes } from "../../../../enums/PropertyTypes";
import { PropertyNames } from "../../../../enums/PropertyNames";
import { usePermissions } from "../../../../modules/auth/Permissions/PermissionContext";
import WritableList, { Actions } from "../../List/WritableList";
import DocumentList from "../../List/DocumentList";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AddressBook from "../../List/AddressBook";
import { activeTabSignal, movingSidePanelSignal } from "../Layout";
import ChartRenderer from "../../Charts/ChartRenderer";
import StorageSummaryChart from "../../Charts/StorageSummaryChart";

interface IEntityView {
    entityName: string,
    entityId: string | undefined
}

export default function EntityView({ entityName, entityId }: IEntityView) {
    const schema = GetSchema(entityName)
    const permissions = usePermissions();
    const location = useLocation();
    const navigate = useNavigate();
    
    // Find the first visible tab index after permission filtering
    const getFirstVisibleTabIndex = () => {
        if (!schema.children || !permissions) return 0;
        const firstVisibleIndex = schema.children.findIndex(child => 
            child.permissionToView === undefined || child.permissionToView(permissions, entityId)
        );
        return firstVisibleIndex >= 0 ? firstVisibleIndex : 0;
    };
    
    const [activeTab, setActiveTab] = useState<Key>(0);
    const [initialized, setInitialized] = useState(false);

    // Initialize active tab to first visible tab after permissions are loaded
    useEffect(() => {
        if (!initialized && permissions) {
            const hash = location.hash.replace('#', '');
            const tabIndex = parseInt(hash, 10);
            const tabCount = schema.children?.length ?? 0;
            
            if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex < tabCount) {
                // Verify the tab is visible before setting it
                const child = schema.children?.[tabIndex];
                if (child && (child.permissionToView === undefined || child.permissionToView(permissions, entityId))) {
                    setActiveTab(tabIndex);
                    activeTabSignal.value = { index: tabIndex }
                } else {
                    // If the tab from hash is not visible, use the first visible tab
                    const firstVisible = getFirstVisibleTabIndex();
                    setActiveTab(firstVisible);
                    activeTabSignal.value = { index: firstVisible }
                }
            } else {
                // No hash or invalid hash, use first visible tab
                const firstVisible = getFirstVisibleTabIndex();
                setActiveTab(firstVisible);
                activeTabSignal.value = { index: firstVisible }
            }
            setInitialized(true);
        }
    }, [permissions, initialized, location.hash, schema, entityId]);

    // Handle hash changes after initial load
    useEffect(() => {
        if (initialized) {
            const hash = location.hash.replace('#', '');
            const tabIndex = parseInt(hash, 10);
            const tabCount = schema.children?.length ?? 0;
            if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex < tabCount) {
                // Verify the tab is visible before setting it
                const child = schema.children?.[tabIndex];
                if (child && (child.permissionToView === undefined || child.permissionToView(permissions!, entityId))) {
                    setActiveTab(tabIndex);
                    activeTabSignal.value = { index: tabIndex }
                }
            }
        }
    }, [location.hash, initialized, schema, permissions, entityId]);

    const handleTabChange = (key: Key) => {
        const tabChanged = key != activeTab
        setActiveTab(key);
        if (typeof key === 'number') {
            activeTabSignal.value = { index: key }
        }
        if (tabChanged) {
            movingSidePanelSignal.value = { ...movingSidePanelSignal.value, show: false, id: undefined };
            navigate(`#${key}`);
        }
    };

    return (<>
        <TabsComponent />
    </>)

    function TabsComponent() {
        return (<>
            <Tabs className={"tabs"} selectedKey={activeTab} onSelectionChange={handleTabChange}>
                <TabList className={"tabs__wrapper"} >
                    {schema.children?.map((x, i) => {
                        if (x.permissionToView === undefined || x.permissionToView(permissions, entityId)) {
                            return <Tab className={"tabs__tab"} id={i}><p className="button-text button-text--small">{x.displayName}</p></Tab>
                        }
                    })}
                </TabList>
                {schema.children?.map((x, i) => {
                    return GetTabComponent(x, i)
                })}
            </Tabs>
        </>)
    }

    function GetTabComponent(schemaChild: ISchemaChild, i: any) {
        // Skip rendering if user doesn't have permission to view this tab
        if (schemaChild.permissionToView !== undefined && !schemaChild.permissionToView(permissions!, entityId)) {
            return undefined;
        }

        // Make sure we assess the active tab - otherwise all content / data for every tab will be fetched
        if (i !== activeTab) {
            return undefined;
        }

        switch (schemaChild.viewType) {
            case EntityViewTypes.list:
                {
                    const filters = [
                        {
                            propertyName: PropertyNames.entityName,
                            value: entityName,
                            required: false,
                            matchType: FilterMatchType.Equal,
                            type: PropertyTypes.entityName
                        } as IFilter,
                        {
                            propertyName: schemaChild.navPropertyName ?? PropertyNames.id,
                            value: entityId,
                            required: false,
                            matchType: FilterMatchType.Equal,
                            type: PropertyTypes.uuid
                        } as IFilter
                    ].filter(x => x.value)

                    const canAddValue = typeof schemaChild.canAdd === 'function' ? schemaChild.canAdd(permissions!, entityId) : schemaChild.canAdd;
                    return (
                        <TabPanel id={i}>
                            <List parentEntityName={entityName} entityId={entityId} entityName={schemaChild.entityName} cantView={schemaChild.cantView} enableFilters={schemaChild.enableFilters} forcedFilters={filters} searchType={schemaChild.viewType} canAdd={canAddValue} tabView={true} alwaysShowheader={schemaChild.alwaysShowheader} />
                        </TabPanel>
                    )
                }

            case EntityViewTypes.writableList: {
                const resolvedEntityName = schemaChild.entityName;
                const filters = [
                    {
                        propertyName: schemaChild.navPropertyName ?? PropertyNames.id,
                        value: entityId,
                        required: false,
                        matchType: FilterMatchType.Equal,
                        type: PropertyTypes.uuid
                    } as IFilter
                ].filter(x => x.value);

                const writeActions = getWritableListActions(schemaChild.entityName);
                return (
                    <WritableList entityName={resolvedEntityName} i={i} filters={filters} parentId={entityId} actionOverrides={writeActions} canAdd={schemaChild.canAdd} />
                )
            }

            case EntityViewTypes.documentList: {
                const filters = [
                    {
                        propertyName: "Concern",
                        value: schemaChild.entityName,
                        required: false,
                        matchType: FilterMatchType.Equal,
                        type: PropertyTypes.string
                    } as IFilter
                ];
                return (
                    <DocumentList parentEntityName={EntityNames.fileUploads} entityName={schemaChild.entityName} i={i} filters={filters} canAdd={schemaChild.canAdd} />
                )
            }

            case EntityViewTypes.addressBook: {

                const filters = [
                    {
                        propertyName: schemaChild.navPropertyName ?? PropertyNames.id,
                        value: entityId,
                        required: false,
                        matchType: FilterMatchType.Equal,
                        type: PropertyTypes.uuid
                    } as IFilter
                ].filter(x => x.value)

                return (
                    <AddressBook entityName={schemaChild.entityName} i={i} filters={filters} parentId={entityId} canAdd={schemaChild.canAdd} />
                )
            }

            case EntityViewTypes.chart: {
                return (
                    <TabPanel id={i}>
                        <ChartRenderer chartType={schemaChild.dataType} entityName={schemaChild.entityName} parentId={entityId} />
                    </TabPanel>
                )
            }

            case EntityViewTypes.storageSummaryChart: {
                return (
                    <TabPanel id={i}>
                        <StorageSummaryChart chartType={schemaChild.dataType} parentId={entityId} />
                    </TabPanel>
                )
            }
        }
    }

    function getWritableListActions(entityName: string) {
        switch (entityName) {
            case EntityNames.volumes:
            case EntityNames.deliveryMethods:
            case EntityNames.prefixes:
                return [Actions.Add, Actions.Edit, Actions.Delete];
            case EntityNames.roles:
            case EntityNames.costMultipliers:
                return [Actions.Edit];
        }
    }
}